import { useMemo } from "react";

import { useWorkspaceLocale } from "src/contexts/global/WorkspaceContext";
import {
  DEFAULT_LOCALE,
  getDateFormats,
  isSupportedLocale,
  type DateFormats,
  type SupportedLocale,
} from "src/localization/dateFormats";

export type WorkspaceDateFormats = DateFormats & {
  /** Resolved BCP-47 workspace locale used to derive the format strings. */
  locale: SupportedLocale;
};

/**
 * Returns locale-aware date format strings for the current workspace.
 *
 * Usage:
 *   const { short, readable, readableDateTime, locale } = useWorkspaceDateFormats();
 *   moment(date).format(readable); // "DD/MM/YYYY" for en-AU/en-GB, "MM/DD/YYYY" for en-US
 *
 * Format keys:
 *   short              - "DD/MM/YYYY" | "MM/DD/YYYY"
 *   shortDateTime      - "DD/MM/YYYY HH:mm:ss" | "MM/DD/YYYY HH:mm:ss"
 *   readable           - "DD/MM/YYYY" | "MM/DD/YYYY"
 *   readableDateTime   - "hh:mma DD/MM/YYYY" | "hh:mma MM/DD/YYYY"
 *   readableDateTimeFilename - "YYYYMMDD-HHmm" (same across all locales)
 *   columnHeader       - non-breaking-space variant of readable
 *   locale             - workspace BCP-47 tag (for DatePickerNext calendar labels etc.)
 */
const useWorkspaceDateFormats = (): WorkspaceDateFormats => {
  const workspaceLocale = useWorkspaceLocale();

  return useMemo(() => {
    const formats = getDateFormats(workspaceLocale);
    const locale: SupportedLocale =
      workspaceLocale && isSupportedLocale(workspaceLocale)
        ? workspaceLocale
        : DEFAULT_LOCALE;
    return { ...formats, locale };
  }, [workspaceLocale]);
};

export default useWorkspaceDateFormats;
